// AudioUtils streams raw PCM16 little-endian mono frames into an
// AudioWorklet for low-latency, gapless playback. It also:
//
//   - exposes an AnalyserNode for visualization (real audio spectrum),
//   - tracks how many source bytes have been enqueued (bytesEnqueued) and
//     how many have actually been played out (bytesPlayed, reported by the
//     worklet). The UI uses those two counters to align speaker labels and
//     transcripts to the audio the listener is currently hearing, not the
//     audio the server has streamed ahead.

export default class AudioUtils {
  constructor(sampleRate = 24000) {
    this.sampleRate = sampleRate;
    this.audioContext = null;
    this.workletNode = null;
    this.gainNode = null;
    this.analyser = null;

    this.volume = 0.75;
    this.isMuted = false;

    this.initialized = false;
    this.initPromise = null;
    this.shouldAutoPlay = false;
    this.pending = []; // PCM frames queued before init completes
    this.framesPlayed = 0;

    // PCM byte accounting -- both in source-rate bytes (2 per sample).
    this.bytesEnqueued = 0;
    this.bytesPlayed = 0;
  }

  // Must be called from a user gesture (click/touch) to satisfy browser
  // autoplay policy.
  userInteractionHandler() {
    this._ensureContext();
    if (this.audioContext && this.audioContext.state === "suspended") {
      this.audioContext.resume().catch((err) => {
        console.error("AudioContext.resume failed:", err);
      });
    }
  }

  _ensureContext() {
    if (this.audioContext) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    try {
      this.audioContext = new Ctx({ sampleRate: this.sampleRate, latencyHint: "interactive" });
    } catch (err) {
      this.audioContext = new Ctx({ latencyHint: "interactive" });
    }
    console.log(
      `[AudioUtils] requested ${this.sampleRate}Hz, got ${this.audioContext.sampleRate}Hz`
    );

    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = this.isMuted ? 0 : this.volume;

    // AnalyserNode sits between gain and destination so it sees what the
    // listener actually hears (post-volume). fftSize=256 gives 128 freq
    // bins, plenty for ~80 bars.
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.75;

    this.gainNode.connect(this.analyser);
    this.analyser.connect(this.audioContext.destination);

    this.initPromise = this.audioContext.audioWorklet
      .addModule("/pcm-player-worklet.js")
      .then(() => {
        this.workletNode = new AudioWorkletNode(this.audioContext, "pcm-player", {
          numberOfInputs: 0,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          processorOptions: {
            capacitySeconds: 60,
            sourceSampleRate: this.sampleRate,
          },
        });

        // Worklet posts cumulative consumed source-sample count at ~25Hz.
        // We mirror it as bytesPlayed (2 bytes per source sample) so the UI
        // layer can compare to bytesEnqueued without unit confusion.
        this.workletNode.port.onmessage = (e) => {
          const m = e.data;
          if (m && m.type === "progress") {
            this.bytesPlayed = m.consumed * 2;
          }
        };

        this.workletNode.connect(this.gainNode);
        this.initialized = true;

        // Drain any frames queued before the worklet was ready.
        for (const buf of this.pending) {
          this._postFrame(buf);
        }
        this.pending.length = 0;
      })
      .catch((err) => {
        console.error("Failed to load PCM worklet:", err);
      });
  }

  setVolume(level, isMuted = false) {
    this.volume = level / 100;
    this.isMuted = isMuted;
    if (this.gainNode) {
      this.gainNode.gain.value = isMuted ? 0 : this.volume;
    }
  }

  // Accepts an ArrayBuffer containing PCM16 LE mono samples.
  pushPCM(arrayBuffer, autoResume = false) {
    if (!arrayBuffer || arrayBuffer.byteLength < 2) return false;
    this._ensureContext();

    this.bytesEnqueued += arrayBuffer.byteLength;

    if (!this.initialized) {
      this.pending.push(arrayBuffer);
    } else {
      this._postFrame(arrayBuffer);
    }

    this.framesPlayed++;
    if (autoResume || this.shouldAutoPlay) {
      this.resume();
    }
    return true;
  }

  _postFrame(arrayBuffer) {
    if (!this.workletNode) return;
    const evenBytes = arrayBuffer.byteLength - (arrayBuffer.byteLength % 2);
    const samples = new Int16Array(arrayBuffer, 0, evenBytes / 2);
    if (samples.buffer === arrayBuffer && arrayBuffer.byteLength === evenBytes) {
      this.workletNode.port.postMessage({ type: "pcm", samples }, [arrayBuffer]);
    } else {
      this.workletNode.port.postMessage({ type: "pcm", samples: samples.slice() });
    }
  }

  flush() {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: "flush" });
    }
    this.pending.length = 0;
    this.bytesEnqueued = 0;
    this.bytesPlayed = 0;
  }

  resume() {
    this.shouldAutoPlay = true;
    this._ensureContext();
    if (this.audioContext && this.audioContext.state === "suspended") {
      this.audioContext.resume().catch((err) => {
        console.error("AudioContext.resume failed:", err);
      });
    }
  }

  suspend() {
    this.shouldAutoPlay = false;
    if (this.audioContext && this.audioContext.state === "running") {
      this.audioContext.suspend().catch((err) => {
        console.error("AudioContext.suspend failed:", err);
      });
    }
  }

  isPlaying() {
    return (
      this.audioContext &&
      this.audioContext.state === "running" &&
      this.shouldAutoPlay
    );
  }

  // For the visualizer.
  getAnalyser() { return this.analyser; }

  // For the AIRadio's ETA queue.
  getBytesEnqueued() { return this.bytesEnqueued; }
  getBytesPlayed() { return this.bytesPlayed; }

  // Smoothed instantaneous loudness in [0, 1]. Used by the ambient
  // background layer to "breathe" with the audio. Reads the time-domain
  // data so it tracks envelope rather than spectral content.
  getLevel() {
    if (!this.analyser) return 0;
    if (!this._timeData || this._timeData.length !== this.analyser.fftSize) {
      this._timeData = new Uint8Array(this.analyser.fftSize);
    }
    this.analyser.getByteTimeDomainData(this._timeData);
    let sumSq = 0;
    for (let i = 0; i < this._timeData.length; i++) {
      const v = (this._timeData[i] - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / this._timeData.length);
    return Math.min(1, rms * 2.5);
  }
}
